import React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { BaseSettingsTable } from "../components/BaseSettingsTable";
import { BaseSettingsCell } from "../components/BaseSettingsCell";
import { UsersSettingsTab, UsersSettingsTabs } from "./tabs";
import { UserDetailsDialog } from "./UserDetailsDialog";
import { formatPrivileges } from "./formatPrivileges";

export type WebAppPrivileges = {
  spotter: boolean;
  drill_down: boolean;
  search_data: boolean;
};

export type UserRowModel = { id: string } & Partial<{
  email: string;
  name: string;
  roles: string[];
  branchesCount: number;
  businessesCount: number;
  is_root: boolean;
  web_app_privileges: WebAppPrivileges;
}>;

export type SettingsUsersTableProps = {
  accounts: UserRowModel[];
  tableClassName?: string;
  selectedTab: UsersSettingsTab;
  businessId?: string;
};

export function SettingsUsersTable({
  accounts,
  tableClassName = "settings-table",
  selectedTab,
  businessId,
}: SettingsUsersTableProps) {
  const [selectedUser, setSelectedUser] = React.useState<UserRowModel | null>(null);
  const [showInviteDialog, setShowInviteDialog] = React.useState(false);

  const handleRowClick = (user: UserRowModel) => {
    setSelectedUser(user);
    setShowInviteDialog(true);
  };

  const handleCloseDialog = () => {
    setShowInviteDialog(false);
    setSelectedUser(null);
  };

  const columns: ColumnDef<UserRowModel, keyof UserRowModel>[] = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ getValue, row }) => {
        const name = getValue() as string;
        const email = row.original.email as string;
        return BaseSettingsCell.avatar(name || email);
      },
    },
    {
      accessorKey: "email",
      header: "Email",
    },
  ];

  if (selectedTab === UsersSettingsTabs.active) {
    columns.push(
      {
        accessorKey: "is_root",
        header: "BI role",
        cell: ({ getValue }) => (getValue() ? "Admin" : "Normal"),
      },
      {
        accessorKey: "web_app_privileges",
        header: "Privileges",
        cell: ({ row }) => formatPrivileges(row.original.web_app_privileges),
      },
    );
  } else {
    columns.push(
      {
        accessorKey: "roles",
        header: "Foodics roles",
        cell: ({ row }) => row.original.roles?.join(", ") || "None",
      },
      {
        accessorKey: "branchesCount",
        header: "Branches",
        cell: ({ row }) => row.original.branchesCount ?? 0,
      },
    );
  }

  return (
    <>
      <BaseSettingsTable
        data={accounts}
        columns={columns}
        tableClassName={tableClassName}
        onRowClick={handleRowClick}
      />

      {selectedUser && (
        <UserDetailsDialog
          show={showInviteDialog}
          user={selectedUser}
          selectedTab={selectedTab}
          businessId={businessId}
          onCancel={handleCloseDialog}
        />
      )}
    </>
  );
}
